/**
 * The direct-to-Blob-Storage `PUT`, retried with backoff.
 *
 * Extracted from `routes/jobs/useJobPhotos.ts` for M1-44, which is the third
 * upload flow in the app and the second to want the retry. The shape is
 * M1-14's and the reasoning is unchanged: of the three steps in
 * request-SAS → PUT → confirm, **only the PUT is worth retrying**. It is the
 * one carrying the whole file over whatever connection a van has in somebody's
 * driveway; the two JSON round trips either side are small, and a failure in
 * them usually means something is actually wrong rather than briefly flaky.
 *
 * ⚠️ `x-ms-blob-type: BlockBlob` is required by the Put Blob operation itself,
 * not optional metadata — without it the request is rejected.
 *
 * ⚠️ **A SAS grants authorization, not CORS.** If this ever starts failing with
 * no status and no body to read, check the storage account's CORS rules before
 * suspecting the SAS: from here a browser's CORS rejection is indistinguishable
 * from a dropped connection. api/CONVENTIONS.md's M1-14 section has the whole
 * story, and it cost a real afternoon.
 *
 * ⚠️ Not used by `useUploadTechnicianPhoto` (M1-24), which still does a single
 * un-retried PUT. Left alone deliberately: changing it would alter that story's
 * behaviour, which is out of M1-44's scope. It is the obvious third caller if
 * anyone touches that flow.
 */
export async function putWithRetry(uploadUrl: string, file: File, attempts = 3): Promise<void> {
  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type },
        body: file,
      });
      if (response.ok) {
        return;
      }
      lastError = new Error(`Blob upload failed: ${response.status}`);
    } catch (error) {
      lastError = error;
    }
    if (attempt < attempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, 500 * 2 ** attempt));
    }
  }
  throw lastError;
}
